import React, {
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Paper } from "@mui/material";

const SignatureCanvas = forwardRef(
  ({ onSignatureChange, height = 300 }, ref) => {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const drawingRef = useRef(false);
    const lastPosRef = useRef({ x: 0, y: 0 });

    // Initialize canvas
    useEffect(() => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      // Set canvas size
      canvas.width = canvas.offsetWidth;
      canvas.height = height;

      // Set styles
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#000000";

      ctxRef.current = ctx;

      // Clear canvas
      clearCanvas();
    }, [height]);

    useImperativeHandle(ref, () => ({
      clear: () => clearCanvas(),
      toDataURL: (type = "image/png", quality = 1) => {
        return canvasRef.current.toDataURL(type, quality);
      },
      isEmpty: () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        return !imageData.data.some((channel) => channel !== 0);
      },
    }));

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (onSignatureChange) {
        onSignatureChange(null);
      }
    };

    const getPosition = (event) => {
      const canvas = canvasRef.current;
      const rect = canvas.getBoundingClientRect();

      let clientX, clientY;

      if (event.type.includes("mouse")) {
        clientX = event.clientX;
        clientY = event.clientY;
      } else {
        // Touch event
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      }

      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const startDrawing = (pos) => {
      drawingRef.current = true;
      lastPosRef.current = pos;

      const ctx = ctxRef.current;
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const draw = (pos) => {
      if (!drawingRef.current) return;

      const ctx = ctxRef.current;
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();

      lastPosRef.current = pos;

      // Notify parent
      if (onSignatureChange) {
        onSignatureChange(canvasRef.current.toDataURL());
      }
    };

    const stopDrawing = () => {
      if (drawingRef.current) {
        drawingRef.current = false;
        const ctx = ctxRef.current;
        ctx.closePath();
      }
    };

    // Mouse events
    const handleMouseDown = (e) => {
      const pos = getPosition(e);
      startDrawing(pos);
    };

    const handleMouseMove = (e) => {
      const pos = getPosition(e);
      draw(pos);
    };

    const handleMouseUp = () => {
      stopDrawing();
    };

    // Touch events for mobile/tablet
    const handleTouchStart = (e) => {
      e.preventDefault();
      const pos = getPosition(e);
      startDrawing(pos);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const pos = getPosition(e);
      draw(pos);
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      stopDrawing();
    };

    return (
      <Paper
        elevation={0}
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        <Box sx={{ p: 2, textAlign: "center", borderBottom: "1px solid #eee" }}>
          Draw your signature below
          <Box sx={{ fontSize: "0.8rem", color: "#666", mt: 0.5 }}>
            Use mouse on desktop or finger/stylus on touch devices
          </Box>
        </Box>
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            width: "100%",
            height: `${height}px`,
            cursor: "crosshair",
            touchAction: "none",
          }}
        />
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            borderTop: "1px solid #eee",
            color: "#666",
            fontSize: "0.8rem",
          }}
        >
          Draw in the area above
        </Box>
      </Paper>
    );
  }
);

SignatureCanvas.displayName = "SignatureCanvas";

export default SignatureCanvas;
